import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { Txt } from 'components/Txt';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import { animateNext } from 'lib/animateNext';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  selectAseChecklistItemForQuestion,
  selectAseTplChecklistItemsForQuestion,
  selectAseTplQuestion,
} from 'store/assessments/selectors';
import { IconButton } from 'components/IconButton';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { Button } from 'components/Button';
import { View } from 'react-native';
import { ChecklistItemResultEnum } from 'store/assessments/types';
import { NAVIGATE_MODAL_BACK } from 'store/actions';
import { doAseSetChecklistItemResults } from 'store/assessments/actions';
import { useEsc } from 'hooks/useEsc';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { isEqual } from 'lodash';

export const QuickAnswerChecklistModal: React.FC<
  ModalScreenProps<ModalScreens.AseQuickAnswerChecklist>
> = ({ navigation, route }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const { questionId, fieldInstanceId, instructions, actionsAfter } =
    route.params;

  const [editingChecklistItemId, setEditingChecklistItemId] = React.useState<
    number | undefined
  >(undefined);

  const question = useSelector((state) =>
    selectAseTplQuestion(state, questionId),
  );

  const checklistItems = useSelector((state) =>
    selectAseTplChecklistItemsForQuestion(state, questionId),
  );

  const checklistItemResultsFromState = useSelector((state) =>
    selectAseChecklistItemForQuestion(state, questionId, fieldInstanceId),
  );

  const [checklistItemResults, setChecklistItemResults] = React.useState<
    Record<string, ChecklistItemResultEnum | null>
  >(
    Object.fromEntries(
      Object.entries(checklistItemResultsFromState).map(([id, result]) => [
        id,
        result.result,
      ]),
    ),
  );

  const initChecklistItemResultsRef = React.useRef(checklistItemResults);

  const handleSave = () => {
    dispatch(NAVIGATE_MODAL_BACK());
    dispatch(
      doAseSetChecklistItemResults({
        questionId,
        results: checklistItemResults,
        fieldInstanceId,
        actionsAfter,
      }),
    );
  };

  const handleSaveChecklistItemResult = (
    checklistItemId: number,
    newResult: ChecklistItemResultEnum,
  ) => {
    setChecklistItemResults((current) => ({
      ...current,
      [checklistItemId]: newResult,
    }));
    setEditingChecklistItemId(undefined);
  };

  const handleDeleteChecklistItemResult = (checklistItemId: number) => {
    setChecklistItemResults((currentResults) => {
      const newResults = { ...currentResults };
      if (initChecklistItemResultsRef.current[checklistItemId] === undefined) {
        delete newResults[checklistItemId];
      } else {
        newResults[checklistItemId] = null;
      }
      return newResults;
    });
    setEditingChecklistItemId(undefined);
  };

  const isDirty = !isEqual(
    checklistItemResults,
    initChecklistItemResultsRef.current,
  );
  const handleClose = () => {
    if (isDirty) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirty });
  }, [navigation, isDirty]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          onPressCancelButton={handleClose}
          onPressSaveButton={
            Object.keys(checklistItemResults).length ? handleSave : null
          }
          saveButtonText="Done"
          heading="Checklist"
        />
      }
    >
      <Card bodyStyle={{ borderColor: theme.colors.yellow, borderWidth: 1 }}>
        <CardRow icon={ICONS.CircleInfoLight} value={instructions} />
      </Card>
      <CardGeneric>
        <Txt weight="bold" size="large">
          {question.question}
        </Txt>

        {!!question.description && (
          <Txt style={{ color: theme.colors.fieldValueText }}>
            {question.description}
          </Txt>
        )}
      </CardGeneric>

      {!!checklistItems.length && (
        <Card>
          {checklistItems.map((checklistItem) => {
            const checklistItemResult = checklistItemResults[checklistItem.id];
            const resultIsTick =
              checklistItemResult === ChecklistItemResultEnum.tick;
            const resultIsCross =
              checklistItemResult === ChecklistItemResultEnum.cross;
            const resultIsNA =
              checklistItemResult === ChecklistItemResultEnum.dash;
            return (
              <CardRow
                key={checklistItem.id}
                style={{ backgroundColor: 'white' }}
                onPress={
                  !checklistItemResult
                    ? undefined
                    : editingChecklistItemId === checklistItem.id
                      ? () => setEditingChecklistItemId(undefined)
                      : () =>
                          animateNext(() => {
                            setEditingChecklistItemId(checklistItem.id);
                          })
                }
                navigationIcon={
                  editingChecklistItemId === checklistItem.id ||
                  !checklistItemResult
                    ? undefined
                    : resultIsTick
                      ? ICONS.CheckLight
                      : resultIsCross
                        ? ICONS.XmarkLight
                        : ICONS.DashLight
                }
                navigationIconColor={
                  resultIsTick
                    ? theme.colors.green
                    : resultIsCross
                      ? theme.colors.red
                      : resultIsNA
                        ? 'black'
                        : undefined
                }
              >
                <Txt>{checklistItem.text}</Txt>
                {(editingChecklistItemId === checklistItem.id ||
                  !checklistItemResult) && (
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      marginTop: theme.paddingHorizontal,
                      backgroundColor: '#f1f1f1',
                      padding: theme.paddingHorizontal / 2,
                      borderRadius: 4,
                      marginHorizontal: -theme.paddingHorizontal / 2,
                      marginBottom: -theme.paddingHorizontal / 2,
                    }}
                  >
                    <ViewWithSeparators
                      horizontal
                      style={{
                        flex: 1,
                        borderWidth: 1,
                        borderColor: '#ddd',
                        borderRadius: 4,
                        overflow: 'hidden',
                        marginRight: !checklistItemResult
                          ? 0
                          : theme.paddingHorizontal / 2,
                      }}
                      separator={
                        <View style={{ width: 1, backgroundColor: '#ddd' }} />
                      }
                    >
                      <Button
                        icon={ICONS.CheckLight}
                        iconColor={resultIsTick ? 'white' : theme.colors.green}
                        style={{
                          backgroundColor: resultIsTick
                            ? theme.colors.green
                            : 'white',
                          borderWidth: 0,
                          flex: 1,
                          borderRadius: 0,
                          justifyContent: 'center',
                        }}
                        styleHovered={{
                          backgroundColor: resultIsTick
                            ? theme.colors.green80
                            : theme.colors.green10,
                        }}
                        stylePressed={{
                          backgroundColor: resultIsTick
                            ? theme.colors.green90
                            : theme.colors.green20,
                        }}
                        onPress={() =>
                          handleSaveChecklistItemResult(
                            checklistItem.id,
                            ChecklistItemResultEnum.tick,
                          )
                        }
                      />
                      <Button
                        icon={ICONS.XmarkLight}
                        iconColor={resultIsCross ? 'white' : theme.colors.red}
                        style={{
                          backgroundColor: resultIsCross
                            ? theme.colors.red
                            : 'white',
                          borderWidth: 0,
                          flex: 1,
                          borderRadius: 0,
                          justifyContent: 'center',
                        }}
                        styleHovered={{
                          backgroundColor: resultIsCross
                            ? theme.colors.red80
                            : theme.colors.red10,
                        }}
                        stylePressed={{
                          backgroundColor: resultIsCross
                            ? theme.colors.red90
                            : theme.colors.red20,
                        }}
                        onPress={() =>
                          handleSaveChecklistItemResult(
                            checklistItem.id,
                            ChecklistItemResultEnum.cross,
                          )
                        }
                      />
                      <Button
                        icon={ICONS.DashLight}
                        style={{
                          backgroundColor: resultIsNA ? '#999' : 'white',
                          borderWidth: 0,
                          flex: 1,
                          borderRadius: 0,
                          justifyContent: 'center',
                        }}
                        iconSize={22}
                        iconColor={resultIsNA ? 'white' : 'black'}
                        styleHovered={{
                          backgroundColor: resultIsNA
                            ? '#999999cc'
                            : '#9999991a',
                        }}
                        stylePressed={{
                          backgroundColor: resultIsNA
                            ? '#999999e6'
                            : '#99999932',
                        }}
                        onPress={() =>
                          handleSaveChecklistItemResult(
                            checklistItem.id,
                            ChecklistItemResultEnum.dash,
                          )
                        }
                      />
                    </ViewWithSeparators>
                    {!!checklistItemResult && (
                      <IconButton
                        icon={ICONS.TrashCanLight}
                        onPress={() =>
                          handleDeleteChecklistItemResult(checklistItem.id)
                        }
                        style={{ marginVertical: -1 }}
                      />
                    )}
                  </View>
                )}
              </CardRow>
            );
          })}
        </Card>
      )}
    </PageScrollView>
  );
};
